import { Component, OnInit } from '@angular/core';
import { DetailedContext } from '@igo2/context';
import { AnyLayerOptions, DataSource, DataSourceService, IgoMap, LayerService, MapBrowserComponent, QueryableDataSourceOptions, VectorLayer } from '@igo2/geo';
import { ContextState } from '@igo2/integration';
import { Point } from 'ol/geom';
import { BehaviorSubject, take } from 'rxjs';
import { Feature } from 'ol';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { StorageService } from '@igo2/core/storage';
import { LanguageService } from '@igo2/core/language';
import { CommonModule } from '@angular/common';
import * as olstyle from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import olClusterSource from 'ol/source/Cluster';
import olVectorSource from 'ol/source/Vector';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { MatInputModule } from '@angular/material/input';

@Component({
    selector: 'app-gdf-impression-bail',
    templateUrl: './gdf-impression-bail.component.html',
    styleUrls: ['./gdf-impression-bail.component.scss'],
    imports: [
        TranslateModule,
        CommonModule,
        MatInputModule,
        MatButtonModule,
        MapBrowserComponent
    ]
})
export class GdfImpressionBailComponent implements OnInit {
  private point: Point;
  public otherMap: IgoMap;
  currentYear = new Date().getFullYear();
  mapYear$ = new BehaviorSubject<string>('');
  entryToPrint: any;
  public defaultStyle: olstyle.Style | ((feature, resolution) => olstyle.Style);
  temporaryDroitWmsLayerLegend: string = '';
  mapScreenshotUrl: string = '';
  mapScaleScreenshotUrl: string = '';
  today = new Date();
  get pageTitle() {
    let titlePart1 = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.titreBail');
    let titlePart2 = '';
    if (this.entryToPrint?.Fins) {
      //if the first character is not a number, return the text as it is
      if (isNaN(Number(this.entryToPrint.Fins.charAt(0)))) {
        titlePart2 = this.entryToPrint.Fins;
      } else {
        let indexOfFirstDash = this.entryToPrint.Fins.indexOf('-');
        if (indexOfFirstDash !== -1) {
          titlePart2 = this.entryToPrint.Fins.substring(indexOfFirstDash);
        } else {
          titlePart2 = this.entryToPrint.Fins;
        }
      }
    }
    return titlePart1 + titlePart2;
  }

  constructor(
    private dataSourceService: DataSourceService,
    private layerService: LayerService,
    private storageService: StorageService,
    private contextState: ContextState,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    //get the map screenshot image
    this.mapScreenshotUrl = this.storageService.get('sigtGdfMapScreenshot') as string;
    //get the map scale screenshot image
    this.mapScaleScreenshotUrl = this.storageService.get('sigtGdfMapScaleScreenshot') as string;
    //get the entry to print
    const entryToPrintFromStore = this.storageService.get('sigtGdfToPrint');
    if (entryToPrintFromStore) {
      this.entryToPrint = JSON.parse(entryToPrintFromStore.toString());
      this.entryToPrint.noDossier = this.entryToPrint[
        this.languageService.translate.instant('sigt.integration.rechercheGdf.form.droitGdf.noDossier')
      ];
      this.contextState.context$.subscribe(context => {
        if (context) {
          //create the secondary map
          this.createOtherMap(context);
          this.addPointToMap(this.otherMap);
        }
      });
    }
  }

  createOtherMap(mapConfig: DetailedContext) {
    this.otherMap = new IgoMap({
      view: mapConfig.map.view
    });
    this.addLayerToMap(this.otherMap, mapConfig.layers.find(l => l.name === 'fond_osm'));
    this.addLayerToMap(this.otherMap, mapConfig.layers.find(l => l.name === 'RegionAdministratif'));
  }

  addLayerToMap(map: IgoMap, layerConfig: AnyLayerOptions) {
    this.layerService.createAsyncLayer(layerConfig).subscribe(layer => {
      layer.visible = true;
      map.layerController.add(layer);
    });
  }

  addPointToMap(map: IgoMap, withLabel: boolean = false) {
    const geoJson = new GeoJSON();
    var geom = geoJson.readGeometry({
      type: "Point",
      coordinates: [this.entryToPrint.Longitude, this.entryToPrint.Latitude]
    }) as Point;
    this.point = geom.transform(this.entryToPrint.geometry_projection, map.projectionCode) as Point;
    if (withLabel) {
      const coordonnees = `${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.coordonnees')} ${this.entryToPrint.projection === 'M' ? 'MTM' : 'UTM'} ${this.entryToPrint.fuseau}`;
      const nord = `${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.nord')} ${this.entryToPrint.coordNord}`;
      const est = `${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.est')} ${this.entryToPrint.coordEst}`;
      this.point.setProperties({
        description: coordonnees + '\n' + nord + '\n' + est
      });
    }
    map.ol.getView().fit(this.point.getExtent());
    const olFeature = new Feature({
      geometry: this.point
    });
    this.tryAddFeaturesToMap([olFeature], map);
  }

  tryAddFeaturesToMap(features: Feature[], map: IgoMap) {
    this.defaultStyle = (feature, _resolution) => {
      return new olstyle.Style({
        stroke: new olstyle.Stroke({
          width: 1,
          color: [0, 0, 0, 1]
        }),
        fill: new olstyle.Fill({
          color: [89, 200, 20, 0.8]
        }),
        image: new olstyle.Circle({
          radius: 4,
          stroke: new olstyle.Stroke({
            width: 1,
            color: [0, 0, 0, 1]
          }),
          fill: new olstyle.Fill({
            color: [89, 200, 20, 0.8]
          })
        }),
        text: new olstyle.Text({
          font: '12px Calibri,sans-serif',
          overflow: true,
          fill: new olstyle.Fill({
            color: '#000'
          }),
          stroke: new olstyle.Stroke({
            color: '#fff',
            width: 3
          }),
          textAlign: 'left',
          justify: 'left',
          offsetY: 3,
          offsetX: 20,
          text: feature.getGeometry().get('description')
        })
      });
    };

    this.dataSourceService
      .createAsyncDataSource({
        type: 'vector',
        queryable: true
      } as QueryableDataSourceOptions)
      .pipe(take(1))
      .subscribe((dataSource: DataSource) => {
        const olLayer = this.layerService.createLayer({
          isIgoInternalLayer: true,
          title: 'Zone',
          source: dataSource,
          visible: true,
          zIndex: 100,
          style: this.defaultStyle
        }) as VectorLayer;

        const ol = dataSource.ol as olVectorSource | olClusterSource;
        ol.addFeatures(features);
        //olLayer.ol.setStyle(this.defaultStyle);
        map.layerController.add(olLayer);
      });
  }

  async generatePDFWithJsPdf() {
    const canvas = await html2canvas(document.getElementById('divToPrintByJsPdf'),
      {
        useCORS: true,
        allowTaint: true
      }).catch((e) => {
        console.log(e);
      });
    if (canvas) {
      var img = canvas.toDataURL("image/png");
      var doc = new jsPDF({
        format: 'legal',
        unit: 'in'
      });

      const format = 'PNG';
      const xInches = 0.393701;
      const yInches = 1.1811;

      this.addImageWithAspectRatio(doc, img, format, xInches, yInches, 7.847, null);

      // doc.addImage(img,
      //   'PNG',
      //   0.393701,//1cm from left
      //   1.1811,//3 cm from top
      //   7.847,//7.712598,//Width remaining after removing 1cm from left and right of a legal paper (8.5inches)
      //   9.23101573125//TODO: can enhance by calculating width/height
      // // ratio of the image and mutliplying it by width in inch (previous parameter)
      // );
      setTimeout(() => {
        doc.save(`${this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.dossier')}-${this.entryToPrint.noDossier}.pdf`);
      }, 1000);
    }
  }

  addImageWithAspectRatio(doc, imageData, format, x, y, desiredWidthInches, desiredHeightInches) {
    const img = new Image();
    img.src = imageData;

    img.onload = () => {
      const originalWidthPixels = img.width;
      const originalHeightPixels = img.height;

      let widthInches = desiredWidthInches;
      let heightInches = desiredHeightInches;

      if (desiredWidthInches && !desiredHeightInches) {
        heightInches = (desiredWidthInches / originalWidthPixels) * originalHeightPixels;
      } else if (desiredHeightInches && !desiredWidthInches) {
        widthInches = (desiredHeightInches / originalHeightPixels) * originalWidthPixels;
      } else if (!desiredWidthInches && !desiredHeightInches) {
        widthInches = originalWidthPixels / 72;// 72 points per inch, default jsPDF
        heightInches = originalHeightPixels / 72;
      }

      doc.addImage(imageData, format, x, y, widthInches, heightInches);
    };
  }
}
